// SubCategorySelectionModal.tsx
import React from "react";
import { Lock, X } from "lucide-react";
import { getCategoryIcon } from "../../utils/getCategoryIcon";
import { localizedField } from "../../utils/localizedField";
import { useAppLanguage } from "../../utils/appLanguage";
import { useTranslation } from "react-i18next";

interface SubCategory {
  id: string;
  name: string;
  name_th: string;
  name_es: string;
  name_id?: string;
  name_ar?: string;
  description?: string;
  description_th?: string;
  description_es?: string;
  description_id?: string;
  description_ar?: string;
  icon: string;
  color?: string;
  /** True when the user's plan does not include this tab. */
  locked?: boolean;
}

interface SubCategorySelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (subCategoryId: string, categoryName: string) => void;
  categoryName: string;
  subCategories: SubCategory[];
}

const SubCategorySelectionModal: React.FC<SubCategorySelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  categoryName,
  subCategories,
}) => {
  const appLanguage = useAppLanguage();
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-[#1a131f] border border-white/10 rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-[#f7f3fa]">
              {t("dialog.chooseReading")}
            </h2>
            <p className="text-sm text-[#c8bfd1] mt-1">
              Choose a specific area within "{categoryName}"
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-[#c8bfd1]" />
          </button>
        </div>

        {/* SubCategories List */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {subCategories.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[#c8bfd1]">No subcategories available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {subCategories.map((subCategory) => {
                const Icon = getCategoryIcon(subCategory.icon);
                const color = "#A78BFF"; // one-accent Astria system (ignores per-category colours)

                return (
                  <button
                    key={subCategory.id}
                    onClick={() => onSelect(subCategory.id, subCategory.name)}
                    className="w-full text-left p-4 rounded-xl border border-white/10 bg-[#221a29] hover:border-[#A78BFF]/60 hover:bg-[#A78BFF]/10 transition-all duration-200 hover:shadow-sm group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110"
                        style={{
                          backgroundColor: `${color}15`,
                          border: `2px solid ${color}30`,
                        }}
                      >
                        <Icon
                          size={24}
                          style={{ color: color }}
                          className="transition-transform group-hover:scale-110"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3
                          className="font-medium text-sm"
                          style={{ color: color }}
                        >
                          {localizedField(subCategory, "name", appLanguage)}
                        </h3>
                        {subCategory.description && (
                          <p className="text-sm text-[#c8bfd1] mt-1 line-clamp-2">
                            {localizedField(subCategory, "description", appLanguage)}
                          </p>
                        )}
                      </div>
                      {subCategory.locked && (
                        <Lock size={16} className="flex-shrink-0 text-[#c8bfd1]" aria-label="Locked" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] border-t border-white/10 bg-[#150f1a] flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-3 px-4 border border-white/15 rounded-xl text-[#f7f3fa] font-medium hover:bg-white/10 transition-colors"
          >
            {t("auth.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubCategorySelectionModal;
